/**
 * Client mirror of `validateCustomerPhoneShape` in
 * `backend/internal/handlers/customers.go`. Both must agree — the server is
 * the authority (it returns `invalid_phone`), this exists so the cashier gets
 * the message inline instead of mid-save.
 *
 * Deliberately NOT the storefront's Pakistani-mobile policy (`requirePKMobile`).
 * Staff-side flows take foreign numbers on purpose, so this only rejects values
 * that are not phone numbers at all: "+1 (415) 555-0123" passes, "asdf" and
 * "call his brother" do not.
 *
 * Why it matters: every staff path that captures a guest phone funnels into
 * `resolveCustomerInTx`, which inserts a customers row. When the value can't be
 * normalised it stores `phone_normalized = NULL` — so that row can never dedup
 * against the same guest's next visit, and their loyalty punches split across
 * two profiles.
 */

/** Must stay in step with `maxCustomerPhoneLen` (Go) and under VARCHAR(40). */
export const MAX_CUSTOMER_PHONE_LEN = 32

/** Must stay in step with `minCustomerPhoneDigits` (Go). */
export const MIN_CUSTOMER_PHONE_DIGITS = 7

/**
 * Returns an operator-facing message when the value is not a usable phone
 * number, or `null` when it is fine. Blank is fine — a guest without a phone
 * is normal and must never block a sale.
 */
export function customerPhoneShapeError(raw: string): string | null {
  const v = raw.trim()
  if (v === '') return null
  if (v.length > MAX_CUSTOMER_PHONE_LEN) {
    return `Phone number is too long (max ${MAX_CUSTOMER_PHONE_LEN} characters).`
  }
  if (!/^[0-9+\-() ]+$/.test(v)) {
    return 'Phone number can only contain digits and + - ( ) spaces.'
  }
  const digits = v.replace(/[^0-9]/g, '').length
  if (digits < MIN_CUSTOMER_PHONE_DIGITS) {
    return `Phone number needs at least ${MIN_CUSTOMER_PHONE_DIGITS} digits.`
  }
  return null
}
